import React, { useState, useEffect } from "react";
import { MdClose, MdCheck } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ModalHeader, ScrollableContent } from "../Styles/InputStyles";
import { GoSearch } from "react-icons/go";

export default function SelectRoom({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedItem = null,
    title = "Select Item",
    searchPlaceholder = "Search...",
    emptyMessage = "No items available",
    noResultsMessage = "No items found",
    showClearSelection = true,
    renderItem = null,
    idKey = "id",
    nameKey = "name",
    descriptionKeys = [],
    enableSearch = true,
    isLoading = false,
}) {
    const [tempSelectedItem, setTempSelectedItem] = useState(selectedItem);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (isOpen) {
            setTempSelectedItem(selectedItem);
            setSearchText("");
        }
    }, [isOpen, selectedItem]);

    const handleItemSelect = (item) => {
        setTempSelectedItem(item);
        onSelect(item);
        onClose();
    };

    const filteredOptions = options.filter(item =>
        item[nameKey]?.toLowerCase().includes(searchText.toLowerCase()) ||
        descriptionKeys.some(key => {
            if (Array.isArray(key)) {
                return key.some(k => item[k]?.toLowerCase().includes(searchText.toLowerCase()))
            }
            return item[key]?.toLowerCase().includes(searchText.toLowerCase())
        })
    );

    const clearSelection = () => {
        setTempSelectedItem(null);
        onSelect(null);
        onClose();
    };

    const getDescriptionValue = (item, key) => {
        if (Array.isArray(key)) {
            for (const k of key) {
                if (item[k]) return item[k];
            }
            return null;
        }
        return item[key];
    };

    const defaultRenderItem = (item, isSelected) => {
        const descriptionValues = descriptionKeys
            .map(key => getDescriptionValue(item, key))
            .filter(value => value != null && value !== "");

        const vacantBeds = item.vacantBeds || 0;

        return (
            <div className="flex flex-col flex-1">
                <div className="flex items-center gap-2">
                    <Label className="font-medium text-sm">
                        {item[nameKey]}
                    </Label>
                    <span className={`text-xs px-2 py-1 rounded-full ${vacantBeds === 0
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-blue-100 text-blue-700'
                        }`}>
                        {vacantBeds} bed{vacantBeds !== 1 ? 's' : ''}
                    </span>
                </div>
                {descriptionValues.length > 0 && (
                    <div className="text-xs mt-1 text-gray-500">
                        {descriptionValues.map((value, index) => (
                            <span key={index}>
                                {value}
                                {index < descriptionValues.length - 1 && " • "}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const isSelected = (itemId) => {
        return tempSelectedItem?.[idKey] === itemId;
    };

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper>
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">{title}</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                {/* Search Box */}
                {enableSearch && (
                    <div className="p-4 relative">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                            />
                            {/* Search Icon */}
                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                <GoSearch className="w-4 h-4 text-gray-400" />
                            </div>
                        </div>
                    </div>
                )}

                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {options.length === 0 ? emptyMessage : noResultsMessage}
                        </div>
                    ) : (
                        <div className="px-4 space-y-2">
                            {filteredOptions.map((item) => {
                                const itemSelected = isSelected(item[idKey]);

                                return (
                                    <OptionRow
                                        key={item[idKey]}
                                        onClick={() => handleItemSelect(item)}
                                        selected={itemSelected}
                                    >
                                        {renderItem ?
                                            renderItem(item, itemSelected) :
                                            defaultRenderItem(item, itemSelected)
                                        }
                                        {itemSelected && (
                                            <MdCheck className="text-green-500 flex-shrink-0 ml-2" size={20} />
                                        )}
                                    </OptionRow>
                                );
                            })}
                        </div>
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
}

const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`;
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const OptionRow = tw.div`
    flex items-center justify-between p-4 border border-gray-200 rounded-lg
    ${props => props.selected
        ? 'border-green-500 bg-green-50 cursor-pointer hover:bg-green-100'
        : 'cursor-pointer hover:bg-gray-50'
    }
`;